// Mint-TMS · tab "Điều độ hôm nay": số liệu tổng quan tính từ danh sách chuyến (chỉ số chuyến, không tiền).
import { LAST_STEP, filterTrips, isDone, type TripFilter } from './trip';
import type { Trip } from './types';

/** Chuyến chưa gán xe (hạn 16:00 hôm nay), nguyên văn board. */
export interface UnassignedTrip {
  id: string;
  desc: string;
  urgent: boolean;
}

export const UNASSIGNED: readonly UnassignedTrip[] = [
  { id: 'TR-2610-0322', desc: 'HANA-SE-2610-0145 · 1×40HC · KCN Sóng Thần → CY Cát Lái 23/Oct 06:00', urgent: true },
  { id: 'TR-2610-0323', desc: 'HANA-SI-2610-0091 · 1×20DC · Cát Lái → Bình Dương 23/Oct 08:00', urgent: false },
];

/** Sản lượng 14 ngày (minh hoạ theo board: 09/Oct → 22/Oct); ngày cuối = số chuyến hôm nay. */
const VOL = [9, 10, 8, 11, 10, 9, 4, 10, 11, 9, 12, 10, 5];
const VOL_OUT = [3, 3, 2, 4, 3, 3, 1, 3, 4, 2, 4, 3, 1];

export const isOutside = (t: Pick<Trip, 'owner'>): boolean => t.owner !== 'HANA';
export const isRisky = (t: Pick<Trip, 'risk'>): boolean => t.risk === 'p0' || t.risk === 'late';

export interface Kpi {
  key: 'all' | 'run' | 'risk' | 'unassigned';
  label: string;
  value: number;
  unit: string;
  tone: 'tx' | 'err' | 'warn';
  /** bộ lọc bảng khi bấm; null = sang tab Kế hoạch */
  filter: TripFilter | null;
}

export function kpis(trips: readonly Trip[], unassigned: readonly UnassignedTrip[]): Kpi[] {
  const own = trips.filter((t) => !isOutside(t)).length;
  const late = trips.filter((t) => t.risk === 'late').length;
  const p0 = trips.filter((t) => t.risk === 'p0').length;
  return [
    { key: 'all', label: 'Chuyến hôm nay', value: trips.length, unit: `${own} xe nhà · ${trips.length - own} nhà xe`, tone: 'tx', filter: 'all' },
    { key: 'run', label: 'Đang chạy', value: filterTrips(trips, 'run').length, unit: 'GPS trực tiếp', tone: 'tx', filter: 'run' },
    { key: 'risk', label: 'Rủi ro', value: p0 + late, unit: `${p0} sự cố · ${late} trễ`, tone: 'err', filter: 'risk' },
    { key: 'unassigned', label: 'Chưa gán xe', value: unassigned.length, unit: 'hạn 16:00', tone: 'warn', filter: null },
  ];
}

export interface ProgressSlice {
  key: 'idle' | 'run' | 'risk' | 'done';
  label: string;
  n: number;
  filter: TripFilter;
  /** cung vòng: dash / offset theo board KC.donut (r52) */
  dash: string;
  off: string;
}

const CC = 2 * Math.PI * 52;

/** 4 phần tiến độ của vòng tròn; rủi ro tính trước các nhóm còn lại. */
export function progress(trips: readonly Trip[]): ProgressSlice[] {
  const idle = trips.filter((t) => t.step === 0 && !isRisky(t)).length;
  const run = trips.filter((t) => t.step > 0 && t.step < LAST_STEP && !isRisky(t)).length;
  const risk = trips.filter(isRisky).length;
  const done = trips.filter((t) => isDone(t) && !isRisky(t)).length;
  const defs: Array<[ProgressSlice['key'], string, number, TripFilter]> = [
    ['idle', 'Chưa chạy', idle, 'all'],
    ['run', 'Đang chạy', run, 'run'],
    ['risk', 'Rủi ro', risk, 'risk'],
    ['done', 'Xong', done, 'done'],
  ];
  const total = defs.reduce((a, d) => a + d[2], 0) || 1;
  let acc = 0;
  return defs.map(([key, label, n, filter]) => {
    const len = (n / total) * CC;
    const gap = len > 4 ? 3 : 0;
    const slice = { key, label, n, filter, dash: `${Math.max(0, len - gap).toFixed(1)} ${(CC - len + gap).toFixed(1)}`, off: (-acc).toFixed(1) };
    acc += len;
    return slice;
  });
}

export interface OnTime {
  pct: number;
  onTime: number;
  late: number;
  /** dashoffset trên cung nửa vòng 251,3 */
  offset: string;
}

export function onTime(trips: readonly Trip[]): OnTime {
  const late = trips.filter(isRisky).length;
  const p = trips.length ? (trips.length - late) / trips.length : 1;
  return { pct: Math.round(p * 100), onTime: trips.length - late, late, offset: (251.3 * (1 - Math.max(0, Math.min(1, p)))).toFixed(1) };
}

export interface LinePts {
  line: string;
  area: string;
  last: { x: number; y: number; v: number };
  pts: Array<{ x: number; y: number; v: number }>;
}

/** Đường 460×118 của board (mn 0, mx 14, x 18→442, y 94→12). */
export function linePath(vals: readonly number[], o = { mn: 0, mx: 14, x0: 18, x1: 442, y0: 12, y1: 94 }): LinePts {
  const r = o.mx - o.mn || 1;
  const n = Math.max(1, vals.length - 1);
  const pts = vals.map((v, i) => ({ x: +(o.x0 + (i * (o.x1 - o.x0)) / n).toFixed(1), y: +(o.y1 - ((v - o.mn) / r) * (o.y1 - o.y0)).toFixed(1), v }));
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p.x} ${p.y}`).join(' ');
  const last = pts[pts.length - 1]!;
  return { line, area: `${line} L${last.x} ${o.y1} L${pts[0]!.x} ${o.y1} Z`, pts, last };
}

export interface Volume {
  all: LinePts;
  outside: string;
  avg: number;
  /** nhãn trục x: ngày 09/Oct + chỉ số 0, 4, 9, 13 */
  axis: Array<{ x: number; k: string }>;
}

export function volume(trips: readonly Trip[]): Volume {
  const all = linePath([...VOL, trips.length]);
  const outside = linePath([...VOL_OUT, trips.filter(isOutside).length]).line;
  const sum = [...VOL, trips.length].reduce((a, b) => a + b, 0);
  const axis = [0, 4, 9, 13].map((i) => ({ x: all.pts[i]!.x, k: `${String(9 + i).padStart(2, '0')}/Oct` }));
  return { all, outside, avg: sum / (VOL.length + 1), axis };
}

/** Bước đang làm + màu theo mức rủi ro của một chuyến, 7 vạch. */
export type StepTone = 'done' | 'bad' | 'ok' | 'late' | 'run' | 'todo';
export function stepTones(t: Pick<Trip, 'step' | 'risk'>): StepTone[] {
  return Array.from({ length: LAST_STEP + 1 }, (_, j): StepTone => {
    if (j < t.step) return t.risk === 'p0' && j === t.step - 1 ? 'bad' : 'done';
    if (j === t.step) return t.step === LAST_STEP ? 'ok' : t.risk === 'p0' ? 'bad' : t.risk === 'late' ? 'late' : 'run';
    return 'todo';
  });
}

/** Tìm lệnh theo số lệnh / job / số cont / biển số, bỏ dấu và dấu cách. */
const norm = (v: string) =>
  v
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .replace(/[\s.\-]/g, '');

export const matchTrip = (t: Trip, q: string): boolean => !q || norm([t.id, t.jobId, t.containerNos.join(' '), t.vehicle, t.crew].join(' ')).includes(norm(q));
